import { faGlobeAmericas } from "@fortawesome/pro-regular-svg-icons/faGlobeAmericas";
import { faLanguage } from "@fortawesome/pro-regular-svg-icons/faLanguage";
import { faEnvelope } from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import { faUser } from "@fortawesome/pro-solid-svg-icons/faUser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, FormControl, FormHelperText, ListItemText, MenuItem, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { withStyles, WithStyles } from "@mui/styles";
import AuthService, { AuthenticationOptions } from "api/Auth";
import * as api from "api/Core";
import { FormStyleClasses } from "assets/jss/common-styles";
import { RequestIndicator } from "components/CallServer";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import MuiRegularButton from "components/MuiExtras/Button/MuiRegularButton";
import MuiCompareTextField from "components/MuiExtras/CustomInput/MuiCompareTextField";
import MuiPasswordTextField from "components/MuiExtras/CustomInput/MuiPasswordTextField";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiPaper from "components/MuiExtras/MuiPaper";
import ReactComment from "components/ReactComment";
import { ReactSplitString } from "components/ReactSplitString";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { validateEmail } from "lib/LibUtils";
import { AccountLoginResult } from "models/ApiEnums";
import { BaseGetUserDto, SaveUserRegisterDto, TimezoneRecordDto } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps } from 'react-router-dom';
import { RoutePaths } from "routes/RouteConfig";
import Session from "services/Session";
import { AuthHelpers } from "views/Public/AuthHelpers";
import AgreementCheckbox from "views/System/Components/Agreement";

/////////////////////////////////////////////////////////////////////////////////////

interface UserRegister extends BaseGetUserDto {
    password: string;
    confirmPassword: string;
}

interface IProps {
}

interface IState {
    formCycle: RequestLifeCycle,
    messageError: any,
    lastError: any,
    dto: Partial<UserRegister>,
    validate: {
        name: boolean,
        nameUnique: boolean;
        email: boolean,
        emailUnique: boolean;
        timezoneId: boolean,
        culture: boolean,
        passwordRule: boolean,
        passwordCompare: boolean;
        agreement: boolean;
    };
}

type Props = IProps & RouteComponentProps<IProps> & WithStyles<typeof FormStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class Register extends React.Component<Props, IState> {

    private cultures: KeyValueItem<string, string>[] = [];
    private timezones: TimezoneRecordDto[] = [];
    private authOptions: AuthenticationOptions = null;
    private postDto: SaveUserRegisterDto = {} as SaveUserRegisterDto;
    private authService = new AuthService();
    private checkAgreement: boolean = true;

    ///
    constructor(props: Props) {
        super(props);

        this.state = {
            formCycle: RequestLifeCycle.Init,
            dto: {
                userId: null,
                confirmPassword: null,
                culture: Session.uiCulture,
                email: null,
                name: null,
                password: null,
                recordInfo: null,
                thumbnail: null,
                timezoneId: Session.timezone
            },
            messageError: null,
            lastError: null,
            validate: {
                name: null,
                nameUnique: null,
                email: null,
                emailUnique: null,
                timezoneId: Session.timezone != null,
                culture: Session.uiCulture != null,
                passwordRule: null,
                passwordCompare: null,
                agreement: false
            }
        };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Public.Register." + r);
    }

    ///
    private initialLoad() {

        const dto = {
            timezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
            culture: "pt-BR"
        };

        Promise.all([api.getCultures(), api.getTimezones("pt"), this.authService.getSettings(), this.authService.hasOneLicenseAvailable()])
            .then(([cultures, timezones, settings, hasOneLicenseAvailable]) => {
                this.cultures = cultures;
                this.timezones = timezones;
                this.authOptions = settings.authOptions;

                this.setState({
                    formCycle: !hasOneLicenseAvailable
                        ? RequestLifeCycle.NoUserLicenses
                        : this.authOptions.enableAutoRegister
                            ? RequestLifeCycle.InitSuccess
                            : RequestLifeCycle.PermissionDenied,
                    dto: dto,
                    validate: {
                        email: null,
                        emailUnique: null,
                        name: null,
                        nameUnique: null,
                        timezoneId: dto.timezoneId != null,
                        culture: dto.culture != null,
                        passwordRule: null,
                        passwordCompare: null,
                        agreement: false,
                    }
                }, () => document.getElementById("email")?.focus());
            })
            .catch((err: any) => {
                this.setState({
                    formCycle: RequestLifeCycle.InitFailed
                });
            });
    }

    ///
    private fillToPost() {
        this.postDto.userId = this.state.dto.userId;
        this.postDto.name = this.state.dto.name;
        this.postDto.email = this.state.dto.email;

        this.postDto.timezoneId = this.state.dto.timezoneId;
        this.postDto.culture = this.state.dto.culture;

        this.postDto.thumbnail = null;
        this.postDto.password = this.state.dto.password;
    }

    ///
    private handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.updateRecord(
            event.target.name,
            event.target.type == "checkbox"
                ? (event.target as HTMLInputElement).checked
                : (event.target as HTMLInputElement).valueAsNumber
                    ? (event.target as HTMLInputElement).valueAsNumber
                    : event.target.value
        );
    };

    ///
    private updateRecord(name: string, v: any) {
        const { dto, validate } = this.state;
        switch (name) {
            case "name":
                this.setState({
                    dto: { ...dto, ...{ name: v } },
                    validate: { ...validate, ...{ name: v.length > 0 } }
                });
                break;
            case "email":
                this.setState({
                    dto: { ...dto, ...{ email: v } },
                    validate: { ...validate, ...{ email: v.length > 0 && validateEmail(v) } }
                });
                break;
            case "ddlTimezoneId":
                this.setState({
                    dto: { ...dto, ...{ timezoneId: v } },
                    validate: { ...validate, ...{ timezoneId: v.length > 0 } }
                });
                break;
            case "ddlCulture":
                this.setState({
                    dto: { ...dto, ...{ culture: v } },
                    validate: { ...validate, ...{ culture: v.length > 0 } }
                });
                break;
            case "agreement":
                this.setState({
                    validate: { ...validate, ...{ agreement: v } }
                });
                break;
        }
    }

    ///
    private isValidForm(): boolean {
        const { name, agreement, culture, email, emailUnique, nameUnique, passwordCompare, passwordRule, timezoneId } = this.state.validate;
        return (name && culture && email && emailUnique && nameUnique && passwordCompare && passwordRule && timezoneId && (agreement || !this.checkAgreement));
    }

    ///
    private handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        this.fillToPost();

        this.setState({
            formCycle: RequestLifeCycle.Posting,
        });

        this.authService.register(this.postDto)
            .then((response) => {
                if (response == AccountLoginResult.Valid) {
                    // Redireciona para a Home ou para o local que solicitou autenticação
                    this.props.history.replace(RoutePaths.TimelineStartup);
                }
                else if (response == AccountLoginResult.NotConfirmed) {
                    this.setState({
                        formCycle: RequestLifeCycle.PostSuccess,
                    });
                }
                else {
                    this.setState({
                        formCycle: RequestLifeCycle.PostFailed,
                        messageError: AuthHelpers.translateStatus(response)
                    });
                }
            })
            .catch((error: any) => {
                this.setState({ formCycle: RequestLifeCycle.None, lastError: error });
            });
    }

    ///
    private buildForm() {
        const { dto, validate } = this.state;
        const { classes } = this.props;

        return (
            <Grid container justifyContent="center" spacing={2}>

                <Grid item xs={12} md >
                    <form noValidate autoComplete="off" >

                        <MuiPaper elevation={1}>

                            <Grid container spacing={2} direction="column" >

                                <Grid item >

                                    <Grid container spacing={1} direction="column" >

                                        <Grid item>
                                            {/* Truque para impedir autocomplete */}
                                            <ReactComment text="<input type='password' autocomplete='new-password' />" />

                                            <FormControl fullWidth>
                                                <TextField
                                                    id="email"
                                                    autoFocus={true}
                                                    name="email"
                                                    placeholder={this.res("EmailPlaceholder")}
                                                    title={this.res("EmailPlaceholder")}
                                                    error={validate.email === false || validate.emailUnique === false}
                                                    value={dto == null ? "" : dto.email}
                                                    onBlur={this.checkEmail}
                                                    onChange={(e) => { this.handleChange(e); }}
                                                    margin="normal"
                                                    autoComplete="off"
                                                    fullWidth
                                                    inputProps={{
                                                        maxLength: 255,
                                                    }}
                                                    InputProps={{
                                                        startAdornment: <FontAwesomeIcon icon={faEnvelope} fixedWidth />
                                                    }}
                                                />
                                                {validate.emailUnique === false && <FormHelperText error>{this.res("EmailAlreadyExists")}</FormHelperText>}
                                            </FormControl>

                                            <FormControl fullWidth>
                                                <TextField
                                                    name="name"
                                                    placeholder={this.res("NamePlaceholder")}
                                                    title={this.res("NamePlaceholder")}
                                                    error={validate.name === false || validate.emailUnique === false}
                                                    value={dto == null ? "" : dto.name}
                                                    onBlur={this.checkUsername}
                                                    onChange={(e) => { this.handleChange(e); }}
                                                    margin="normal"
                                                    autoComplete="new-name"
                                                    fullWidth
                                                    inputProps={{
                                                        maxLength: 35,
                                                    }}
                                                    InputProps={{
                                                        startAdornment: <FontAwesomeIcon icon={faUser} fixedWidth />
                                                    }}
                                                />
                                                {validate.nameUnique === false && <FormHelperText error>{this.res("NameAlreadyExists")}</FormHelperText>}
                                            </FormControl>

                                            {
                                                false &&
                                                <FormControl fullWidth>
                                                    <TextField
                                                        select={true}
                                                        placeholder={this.res("TimezonePlaceholder")}
                                                        title={this.res("TimezonePlaceholder")}
                                                        name="ddlTimezoneId"
                                                        error={validate.timezoneId === false}
                                                        value={dto.timezoneId}
                                                        onChange={(e) => { this.handleChange(e); }}
                                                        inputProps={{ name: 'ddlTimezoneId', id: 'LabelTimezone' }}
                                                        margin="normal"
                                                        fullWidth
                                                        InputProps={
                                                            {
                                                                startAdornment: <FontAwesomeIcon icon={faGlobeAmericas} fixedWidth />
                                                            }
                                                        }
                                                    >
                                                        {
                                                            this.timezones.map(
                                                                (v) => (
                                                                    <MenuItem key={v.zoneId} value={v.zoneId}>
                                                                        <ListItemText primary={`${v.zoneId} (${v.name})`} />
                                                                    </MenuItem>
                                                                )
                                                            )
                                                        }
                                                    </TextField>
                                                </FormControl>
                                            }

                                            {
                                                false &&
                                                <FormControl fullWidth>
                                                    <TextField
                                                        select={true}
                                                        placeholder={this.res("CulturePlaceholder")}
                                                        title={this.res("CulturePlaceholder")}
                                                        error={validate.culture === false}
                                                        value={dto.culture}
                                                        onChange={(e) => { this.handleChange(e); }}
                                                        inputProps={{ name: 'ddlCulture', id: 'ddlCulture' }}
                                                        margin="normal"
                                                        fullWidth
                                                        InputProps={{
                                                            startAdornment: <FontAwesomeIcon icon={faLanguage} fixedWidth />
                                                        }}
                                                    >
                                                        {this.cultures.map((v) => (
                                                            <MenuItem key={v.key} value={v.key}>
                                                                <ListItemText primary={v.value} />
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>

                                                </FormControl>
                                            }

                                            <MuiPasswordTextField
                                                label={this.res("PasswordLabel")}
                                                fullWidth={true}
                                                maxLength={16}
                                                minLength={this.authOptions.policies.passwordMinLength}
                                                minNumericChars={this.authOptions.policies.passwordMinNumericChars}
                                                minSpecialChars={this.authOptions.policies.passwordMinSpecialChars}
                                                minLowerChars={this.authOptions.policies.passwordMinLowerChars}
                                                minUpperChars={this.authOptions.policies.passwordMinUpperChars}
                                                margin="dense"
                                                autoComplete="off"
                                                value={this.state.dto.password}
                                                InputLabelProps={
                                                    {
                                                        shrink: true
                                                    }
                                                }
                                                onChange={
                                                    (text, isValid) => {
                                                        this.setState(
                                                            (current) => (
                                                                {
                                                                    dto: { ...current.dto, ...{ password: text } },
                                                                    validate: { ...current.validate, ...{ passwordRule: isValid, passwordCompare: text == current.dto.confirmPassword } }
                                                                }
                                                            )
                                                        );
                                                    }
                                                }
                                            />

                                            <MuiCompareTextField
                                                label={this.res("ConfirmPasswordLabel")}
                                                fullWidth={true}
                                                maxLength={16}
                                                margin="dense"
                                                valueToCompare={this.state.dto.password}
                                                onChange={
                                                    (text) => {
                                                        this.setState(
                                                            (current) => (
                                                                {
                                                                    dto: { ...current.dto, ...{ confirmPassword: text } },
                                                                    validate: { ...current.validate, ...{ passwordCompare: text == current.dto.password } }
                                                                }
                                                            )
                                                        );
                                                    }
                                                }

                                            />

                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <div className={classes.formActionWrapper} >
                                <p>
                                    <AgreementCheckbox
                                        onChange={(checked) => this.updateRecord("agreement", checked)}
                                        onNoAgreementFound={() => this.checkAgreement = false}
                                    />
                                </p>

                                <div className={classes.formActionContainer}>
                                    <Grid container justifyContent="space-between" alignItems="center">
                                        <Grid item>
                                            <MuiRegularButton
                                                type="button"
                                                size="large"
                                                onClick={() => this.props.history.replace(RoutePaths.Login)}
                                            >
                                                <span>{this.props.t("Common.Buttons.BackText")}</span>
                                            </MuiRegularButton>
                                        </Grid >
                                        <Grid item>
                                            <Button
                                                color="primary"
                                                size="large"
                                                variant="contained"
                                                component="span"
                                                disabled={!this.isValidForm()}
                                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.handleSubmit(e)}
                                            >
                                                <span>{this.props.t("Common.Buttons.CreateText")}</span>
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </div>
                            </div>

                        </MuiPaper>

                    </form >
                </Grid>
            </Grid>
        );
    }

    ///
    private buildConfirmationMessage() {
        return (
            <Grid container direction="column" justifyContent="space-around" >
                <Grid item>
                    <MuiMessageBox
                        variant="info"
                        message={
                            <>
                                <Typography variant="h6" color="inherit">
                                    <span>{this.res("RegisterSuccessM1")}</span>
                                </Typography>
                                <Typography variant="body2" color="inherit">
                                    <span>{this.res("RegisterSuccessM2")}</span>
                                </Typography>
                            </>
                        }
                        action={
                            <MuiRegularButton
                                color="primary"
                                size="large"
                                onClick={() => { this.props.history.replace(RoutePaths.Login); }}
                            >
                                <span>{this.props.t("Common.Buttons.OkText")}</span>
                            </MuiRegularButton>
                        }
                    />
                </Grid>
            </Grid>
        );
    }

    ///
    private buildFailedMessage() {
        return (
            <Grid container direction="row" justifyContent="space-around" >
                <Grid item>
                    <MuiMessageBox
                        variant="error"
                        message={
                            <>
                                <Typography variant="h6">
                                    <span>{this.res("RegisterFailedM1")}</span>
                                </Typography>
                                <Typography variant="body2">
                                    <span>{this.state.messageError}</span>
                                </Typography>
                            </>
                        }
                        action={
                            <MuiRegularButton
                                color="primary"
                                size="large"
                                onClick={() => { this.props.history.replace(RoutePaths.Login); }}
                            >
                                <span>{this.props.t("Common.Buttons.OkText")}</span>
                            </MuiRegularButton>
                        }
                    />
                </Grid>
            </Grid>
        );
    }

    ///
    private checkEmail = () => {
        api.checkUserEmailIsUnique(null, this.state.dto.email)
            .then((result) => {
                this.setState(state => {
                    state.validate.emailUnique = result;
                    return state;
                });
            });
    };

    ///
    private checkUsername = () => {
        api.checkUserNameIsUnique(null, this.state.dto.name)
            .then((result) => {
                this.setState(state => {
                    state.validate.nameUnique = result;
                    return state;
                });
            });
    };

    ///
    componentDidMount() {
        if (this.state.formCycle == RequestLifeCycle.Init) {
            this.initialLoad();
        }
    }

    ///
    render() {
        if (this.state.formCycle == RequestLifeCycle.Init)
            return <RequestIndicator type="waiting" />;

        if (this.state.formCycle == RequestLifeCycle.InitFailed)
            return <MuiMessageBox
                variant="error"
                message={this.res("InitFailed")}
            />;


        if (this.state.formCycle == RequestLifeCycle.PermissionDenied)
            return <MuiMessageBox
                variant="error"
                message={this.res("RegisterNotAvailable")}
            />;

        if (this.state.formCycle == RequestLifeCycle.NoUserLicenses)
            return (
                <Grid container alignItems="center" justifyContent="center" direction="column" spacing={2}>
                    <Grid item>
                        <MuiMessageBox
                            variant="warning"
                            message={this.res("NoLicensesAvailable")}
                        />
                    </Grid>
                    <Grid item>
                        <MuiRegularButton
                            type="button"
                            size="large"
                            onClick={() => this.props.history.replace(RoutePaths.Login)}
                        >
                            <span>{this.props.t("Common.Buttons.BackText")}</span>
                        </MuiRegularButton>
                    </Grid>
                </Grid>
            );

        return (
            <>
                {this.state.formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                <RequestErrorDialog
                    open={this.state.lastError != null}
                    error={this.state.lastError}
                    onClose={() => this.setState({ lastError: null })}
                />

                <Grid container justifyContent="center">
                    <Grid item xs={12}  >
                        {
                            this.state.formCycle == RequestLifeCycle.PostFailed
                                ? this.buildFailedMessage()
                                : this.state.formCycle == RequestLifeCycle.PostSuccess
                                    ? this.buildConfirmationMessage()
                                    : this.buildForm()
                        }
                    </Grid>
                </Grid>

            </>);
    }
}

/////////////////////////////////////////////////////////////////////////////////////

const withAllStyles = withStyles(
    (theme) => ({
        ...FormStyleClasses(theme),
    }),
    { withTheme: true },
);

export default withSnackbar(withTranslation()(withAllStyles(Register)));